/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef} from 'react';
import {Box} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import Tooltip from '../components/tooltip/Tooltip';
import {useIsOverflowing} from '../hooks/useIsOverflowing';
import {EventHistoryBadgeIcon} from '../icons/current/EventHistoryBadgeIcon';
import {ThunderIcon} from '../icons/current/ThunderIcon';

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    notificationsContainer : {
      alignItems : 'center',
      display    : 'flex',
      flex       : '0 1 auto',
      height     : '32px',
      padding    : '12px 8px 8px 16px',
      minWidth   : '0',
      overflow   : 'hidden'
    },
    currentnotifications : {
      alignItems   : 'center',
      display      : 'block',
      overflow     : 'hidden',
      whiteSpace   : 'nowrap',
      textOverflow : 'ellipsis'
    },
    notificationsGroup : {
      alignItems           : 'center',
      display              : 'inline-flex',
      height               : '16px',
      padding              : 0,
      verticalAlign        : 'middle',
      '&:not(:last-child)' : {
        marginRight : '4px'
      }
    },
    notificationsText : {
      alignItems    : 'center',
      color         : dlsColor('grey2'),
      display       : 'block',
      fontFamily    : 'Roboto',
      fontSize      : '12px',
      fontWeight    : 400,
      lineHeight    : '14px',
      letterSpacing : '0.5px'
    },
    notificationsIcon : {
      alignItems     : 'center',
      display        : 'flex',
      height         : '16px',
      justifyContent : 'center',
      width          : '16px'
    },
    thunderIcon : {
      alignItems     : 'center',
      display        : 'flex',
      height         : '16px',
      justifyContent : 'center',
      width          : '16px'
    }
  };
});

/**
 * Component for rendering notification card footer
 * 
 * ```typescript
 * 
 * import {NotificationHeaderCard} from '@ion/dls/src/notifications'
 *
 * 
 *
 * ```
 *
 */
export const NotificationCardFooter = () => {
  const classes          = useStyles();
  const notificationsRef = useRef<HTMLDivElement>(null);
  const prefix           = translate('notifications:panel.footer.clearNotificationsPrefix');
  const suffix           = translate('notifications:panel.footer.clearNotificationsSuffix');
  const isOverflowing    = useIsOverflowing(notificationsRef, [prefix, suffix]);

  // builds the notification tip content - isTooltip swaps each icon to
  // white and drops the local text color so it reads correctly against
  // the Tooltip's own dark bubble, instead of duplicating this markup
  // for each context ...
  const renderNotifications = function(isTooltip: boolean) {
    const thunderIconProps = isTooltip ? {color: dlsColor('white')} : {};
    const badgeIconColor   = isTooltip ? dlsColor('white') : dlsColor('grey2');
    const textClassName    = isTooltip ? '' : classes.notificationsText;
    return (
      <div className={classes.notificationsGroup}>
        <span className={classes.thunderIcon}>
          <ThunderIcon {...thunderIconProps} />
        </span>
        <span className={textClassName}>
          {prefix}
        </span>
        <span className={classes.notificationsIcon}>
          <EventHistoryBadgeIcon size={IconSize.small} color={badgeIconColor} />
        </span>
        <span className={textClassName}>
          {suffix}
        </span>
      </div>
    );
  };

  return (
    <Box sx={{
      p         : 2,
      mt        : 'auto',
      height    : '64px',
      padding   : '0 !important',
      boxShadow : '0px -25px 2px 0px rgba(255, 255, 255, 0.5), 0px -2px 4px 0px rgba(0, 0, 0, 0.12)'
    }}>
      <div className={classes.notificationsContainer}>
        <Tooltip title={renderNotifications(true)} placement='top' arrow renderOnlyChildren={!isOverflowing}>
          <div ref={notificationsRef} className={classes.currentnotifications}>
            {renderNotifications(false)}
          </div>
        </Tooltip>
      </div>
    </Box>
  );
};

export default React.memo(NotificationCardFooter);
